import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useT } from "@/lib/i18n";

/* ------------------------------------------------------------------ *
 * Route-pair map (EN ↔ CN).
 *
 * We register BOTH directions here so language toggles are O(1) and
 * so a direct hit on the Chinese URL knows what to show when the user
 * taps "EN". Keep this synchronised with the actual routes declared in
 * App.js — any missing entry falls back to the home page toggle
 * (`/` ↔ `/cn`) so no deep page silently loses its language mapping.
 *
 * Patterns in use:
 *   • Home              /                       ↔  /cn
 *   • Services (legacy) /photo-video-services   ↔  /photo-video-services/cn
 *   • Services (alias)  /services               ↔  /services/cn   (redirect wrappers)
 *   • Offer             /ig/content/AI          ↔  /ig/content/AI/cn
 *   • International     /international          ↔  /international/cn
 *   • About             /about                  ↔  /about/cn
 *   • Journal           /journal                ↔  /journal/cn
 *   • Work list         /work                   ↔  /work/cn
 *   • Member            /member                 ↔  /member/cn
 *   • Subscribe         /subscribe              ↔  /subscribe/cn
 *   • Intent SEO pages  /food-videographer-kuala-lumpur ↔ /food-videographer-kuala-lumpur/cn
 *   • B2B               /b2b                    ↔  /b2b/cn
 * ------------------------------------------------------------------ */
const LANG_PAIRS_EN_TO_CN = {
    "/": "/cn",
    "/work": "/work/cn",
    "/photo-video-services": "/photo-video-services/cn",
    "/services": "/services/cn",
    "/ig/content/AI": "/ig/content/AI/cn",
    "/international": "/international/cn",
    "/about": "/about/cn",
    "/journal": "/journal/cn",
    "/member": "/member/cn",
    "/subscribe": "/subscribe/cn",
    "/b2b": "/b2b/cn",
    "/food-videographer-kuala-lumpur": "/food-videographer-kuala-lumpur/cn",
    "/restaurant-opening-campaign-kl": "/restaurant-opening-campaign-kl/cn",
    "/food-product-video-malaysia": "/food-product-video-malaysia/cn",
    "/menu-photographer-klang-valley": "/menu-photographer-klang-valley/cn",
};
// Build the reverse map once for CN → EN toggles.
const LANG_PAIRS_CN_TO_EN = Object.fromEntries(
    Object.entries(LANG_PAIRS_EN_TO_CN).map(([en, cn]) => [cn, en])
);

function resolveLangTarget(pathname, lang) {
    const p = pathname.replace(/\/+$/, "") || "/";
    if (lang === "cn") {
        // Currently CN → find EN partner
        if (LANG_PAIRS_CN_TO_EN[p]) return LANG_PAIRS_CN_TO_EN[p];
        // Last resort — home.
        return "/";
    }
    // Currently EN → find CN partner
    if (LANG_PAIRS_EN_TO_CN[p]) return LANG_PAIRS_EN_TO_CN[p];
    return "/cn";
}

export default function SiteNav() {
    const location = useLocation();
    const navigate = useNavigate();
    const { lang, t } = useT();
    const [open, setOpen] = useState(false);
    const onHome = location.pathname === "/" || location.pathname === "/cn";
    const homePath = lang === "cn" ? "/cn" : "/";
    const link = (anchor) => (onHome ? `#${anchor}` : `${homePath}#${anchor}`);

    // Close the mobile drawer whenever the route changes.
    useEffect(() => { setOpen(false); }, [location.pathname]);
    // Lock scroll while drawer is open.
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    const toggleLang = () => {
        const target = resolveLangTarget(location.pathname, lang);
        navigate(target + location.hash);
    };

    // Nav link destinations stay in sync with the current language — once
    // the user is on /cn, "About" should route to /about/cn (not /about)
    // so the Chinese context persists across every tap.
    const path = (en) => (lang === "cn" ? (LANG_PAIRS_EN_TO_CN[en] || en) : en);
    const navLinks = [
        { to: path("/work"), label: lang === "cn" ? "作品" : "Work", testid: "nav-work" },
        { to: path("/photo-video-services"), label: lang === "cn" ? "马来西亚服务" : "Malaysia", testid: "nav-malaysia" },
        { to: path("/international"), label: lang === "cn" ? "国际合作" : "International", testid: "nav-international" },
        { to: path("/about"), label: lang === "cn" ? "关于" : "About", testid: "nav-about" },
    ];

    return (
        <header
            data-testid="site-nav"
            className="fixed top-0 inset-x-0 z-50 bg-cafe-cream/85 backdrop-blur-md border-b border-cafe-espresso/8"
        >
            <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14 h-16 md:h-20 flex items-center justify-between">
                <Link to={homePath} className="flex items-baseline gap-3" data-testid="nav-logo">
                    <span className="font-serif text-3xl md:text-[32px] text-cafe-espresso leading-none tracking-tight">
                        kaily<span className="text-cafe-espresso">.</span>
                    </span>
                    <span className="hidden md:inline text-[10.5px] uppercase tracking-[0.28em] text-cafe-espresso/70 font-medium">
                        {lang === "cn" ? "餐饮视觉工作室" : "F&B Visual Studio"}
                    </span>
                </Link>
                <nav className="hidden md:flex items-center gap-9 lg:gap-11 text-[13.5px] text-cafe-espresso/85 font-medium">
                    {navLinks.map(l => (
                        <Link key={l.testid} to={l.to} data-testid={l.testid} className="hover:text-cafe-olive transition-colors">{l.label}</Link>
                    ))}
                    <a href={link("contact")} data-testid="nav-contact" className="hover:text-cafe-olive transition-colors">{lang === "cn" ? "联系" : "Contact"}</a>
                </nav>
                <div className="flex items-center gap-3 md:gap-4">
                    <button
                        onClick={toggleLang}
                        data-testid="lang-toggle"
                        className="hidden md:inline-flex items-center text-[11.5px] uppercase tracking-[0.22em] text-cafe-espresso/60 hover:text-cafe-olive transition-colors"
                        title="Switch language"
                    >
                        {lang === "cn" ? "EN" : "中文"}
                    </button>
                    <a
                        href={link("contact")}
                        data-testid="nav-cta"
                        className="hidden sm:inline-flex items-center h-10 md:h-11 px-5 md:px-6 rounded-full border border-cafe-espresso text-cafe-espresso text-[13px] font-medium hover:bg-cafe-espresso hover:text-cafe-cream transition-colors whitespace-nowrap"
                    >
                        {lang === "cn" ? "开启项目 →" : "Discuss a Project"}
                    </a>
                    {/* Mobile compact International entry — simple accessible link, no auto-redirect */}
                    <Link
                        to={path("/international")}
                        data-testid="nav-international-compact"
                        className="md:hidden inline-flex items-center text-[11px] uppercase tracking-[0.2em] text-cafe-espresso/80 hover:text-cafe-olive border-b border-cafe-espresso/25 pb-0.5"
                    >
                        {lang === "cn" ? "国际" : "International"}
                    </Link>
                    {/* Mobile hamburger */}
                    <button
                        type="button"
                        onClick={() => setOpen(v => !v)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        data-testid="nav-mobile-toggle"
                        className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-cafe-espresso/30 text-cafe-espresso hover:bg-cafe-espresso hover:text-cafe-cream transition-colors"
                    >
                        {open ? <X className="h-4 w-4" strokeWidth={1.75} /> : <Menu className="h-4 w-4" strokeWidth={1.75} />}
                    </button>
                    {/* Hidden IG Content AI link (keeps URL discoverable without cluttering nav) */}
                    <Link
                        to={path("/ig/content/AI")}
                        data-testid="nav-offer"
                        className="sr-only"
                    >
                        IG Content AI
                    </Link>
                </div>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    data-testid="nav-mobile-drawer"
                    className="md:hidden fixed inset-x-0 top-16 z-40 bg-cafe-cream border-t border-cafe-espresso/10 overflow-y-auto"
                    style={{ height: "calc(100vh - 4rem)" }}
                >
                    <nav className="flex flex-col divide-y divide-cafe-espresso/10 px-6">
                        <Link
                            to={path("/work")}
                            data-testid="nav-work-mobile"
                            className="py-5 font-serif text-3xl text-cafe-espresso"
                            onClick={() => setOpen(false)}
                        >
                            {lang === "cn" ? "作品" : "Work"}
                        </Link>
                        {/* Service region group — plain accessible links, no modal */}
                        <div className="py-5">
                            <p className="text-[10.5px] uppercase tracking-[0.28em] text-cafe-espresso/55 mb-3">
                                {lang === "cn" ? "服务地区" : "Service Regions"}
                            </p>
                            <div className="flex flex-col gap-3">
                                <Link
                                    to={path("/photo-video-services")}
                                    data-testid="nav-malaysia-mobile"
                                    className="font-serif text-2xl text-cafe-espresso"
                                    onClick={() => setOpen(false)}
                                >
                                    {lang === "cn" ? "马来西亚服务" : "Malaysia Services"}
                                </Link>
                                <Link
                                    to={path("/international")}
                                    data-testid="nav-international-mobile"
                                    className="font-serif text-2xl text-cafe-olive"
                                    onClick={() => setOpen(false)}
                                >
                                    {lang === "cn" ? "国际合作项目" : "International Projects"}
                                </Link>
                            </div>
                        </div>
                        <Link
                            to={path("/about")}
                            data-testid="nav-about-mobile"
                            className="py-5 font-serif text-3xl text-cafe-espresso"
                            onClick={() => setOpen(false)}
                        >
                            {lang === "cn" ? "关于" : "About"}
                        </Link>
                        <a
                            href={link("contact")}
                            data-testid="nav-contact-mobile"
                            className="py-5 font-serif text-3xl text-cafe-espresso"
                            onClick={() => setOpen(false)}
                        >
                            {lang === "cn" ? "联系" : "Contact"}
                        </a>
                    </nav>
                    <div className="px-6 pt-6 pb-10 flex items-center justify-between">
                        <button
                            onClick={toggleLang}
                            data-testid="lang-toggle-mobile"
                            className="text-[12px] uppercase tracking-[0.22em] text-cafe-espresso/70"
                        >
                            {lang === "cn" ? "English" : "中文"}
                        </button>
                        <a
                            href={link("contact")}
                            data-testid="nav-cta-mobile"
                            onClick={() => setOpen(false)}
                            className="inline-flex items-center h-11 px-6 rounded-full bg-cafe-espresso text-cafe-cream text-[13px] font-medium"
                        >
                            {lang === "cn" ? "开启项目 →" : "Discuss a Project"}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
